import { PaymentProviderType } from '@/types/composition-functions';

/**
 * What the public landing page changes per visiting country: currency,
 * dial code, the payment gateways Gradeverse supports there, school
 * vocabulary and the national exam bodies schools prepare for.
 *
 * Gateways list only providers the backend integrates (PaymentProviderType)
 * and only where that provider operates for merchants. A country with none
 * gets the "tell us your gateway" prompt instead of a promise.
 */
export interface CountryProfile {
  code: string;
  name: string;
  /** Name as it reads mid-sentence: "the United Kingdom". Defaults to name. */
  inSentence?: string;
  currency: string;
  /** BCP 47 locale used to format money and numbers for this country. */
  locale: string;
  dialCode: string;
  gateways: PaymentProviderType[];
  academicPeriod: 'term' | 'semester';
  /** How this country names nursery-to-secondary schooling. */
  schoolLevels: string;
  examBodies: string[];
  /** Sample people for the hero dashboard mock, so it looks local. */
  sampleNames: [string, string, string];
}

/** Used when the visitor's country is unknown or has no profile: USD, neutral copy. */
export const INTERNATIONAL_PROFILE: CountryProfile = {
  code: 'INTL',
  name: 'International',
  currency: 'USD',
  locale: 'en-US',
  dialCode: '',
  gateways: [],
  academicPeriod: 'term',
  schoolLevels: 'K-12',
  examBodies: [],
  sampleNames: ['Daniel Mensah', 'Amara Okafor', 'Priya Sharma'],
};

const { Paystack, Flutterwave, Remita, Interswitch, Monnify } = PaymentProviderType;

export const COUNTRY_PROFILES: readonly CountryProfile[] = [
  {
    code: 'NG', name: 'Nigeria', currency: 'NGN', locale: 'en-NG', dialCode: '+234',
    gateways: [Paystack, Flutterwave, Remita, Interswitch, Monnify],
    academicPeriod: 'term', schoolLevels: 'Nursery, Primary & Secondary',
    examBodies: ['WAEC', 'NECO', 'JAMB'],
    sampleNames: ['Adekunle Okafor', 'Taseyon Godonu', 'Chiamaka Eze'],
  },
  {
    code: 'GH', name: 'Ghana', currency: 'GHS', locale: 'en-GH', dialCode: '+233',
    gateways: [Paystack, Flutterwave],
    academicPeriod: 'term', schoolLevels: 'Basic & Senior High',
    examBodies: ['WAEC (BECE, WASSCE)'],
    sampleNames: ['Kwame Asante', 'Ama Mensah', 'Kofi Boateng'],
  },
  {
    code: 'KE', name: 'Kenya', currency: 'KES', locale: 'en-KE', dialCode: '+254',
    gateways: [Paystack, Flutterwave],
    academicPeriod: 'term', schoolLevels: 'Primary, Junior & Senior School',
    examBodies: ['KNEC (KJSEA, KCSE)'],
    sampleNames: ['Wanjiru Kamau', 'Otieno Odhiambo', 'Achieng Atieno'],
  },
  {
    code: 'ZA', name: 'South Africa', currency: 'ZAR', locale: 'en-ZA', dialCode: '+27',
    gateways: [Paystack, Flutterwave],
    academicPeriod: 'term', schoolLevels: 'Foundation to FET Phase',
    examBodies: ['NSC', 'IEB'],
    sampleNames: ['Thabo Nkosi', 'Lerato Dlamini', 'Sipho Ndlovu'],
  },
  {
    code: 'UG', name: 'Uganda', currency: 'UGX', locale: 'en-UG', dialCode: '+256',
    gateways: [Flutterwave],
    academicPeriod: 'term', schoolLevels: 'Primary & Secondary',
    examBodies: ['UNEB (PLE, UCE, UACE)'],
    sampleNames: ['Nakato Sarah', 'Okello James', 'Namubiru Grace'],
  },
  {
    code: 'TZ', name: 'Tanzania', currency: 'TZS', locale: 'en-TZ', dialCode: '+255',
    gateways: [Flutterwave],
    academicPeriod: 'term', schoolLevels: 'Primary & Secondary',
    examBodies: ['NECTA (PSLE, CSEE, ACSEE)'],
    sampleNames: ['Neema Mwakyusa', 'Baraka Mushi', 'Rehema Juma'],
  },
  {
    code: 'RW', name: 'Rwanda', currency: 'RWF', locale: 'en-RW', dialCode: '+250',
    gateways: [Flutterwave],
    academicPeriod: 'term', schoolLevels: 'Primary & Secondary',
    examBodies: ['NESA'],
    sampleNames: ['Uwase Aline', 'Mugisha Eric', 'Ishimwe Grace'],
  },
  {
    code: 'CM', name: 'Cameroon', currency: 'XAF', locale: 'fr-CM', dialCode: '+237',
    gateways: [Flutterwave],
    academicPeriod: 'term', schoolLevels: 'Primary & Secondary',
    examBodies: ['GCE Board', 'Office du Bac'],
    sampleNames: ['Ngono Marie', 'Tabi Emmanuel', 'Fon Brenda'],
  },
  {
    code: 'SN', name: 'Senegal', currency: 'XOF', locale: 'fr-SN', dialCode: '+221',
    gateways: [Flutterwave],
    academicPeriod: 'semester', schoolLevels: 'Primary & Secondary',
    examBodies: ['BFEM', 'Baccalauréat'],
    sampleNames: ['Awa Diop', 'Moussa Ndiaye', 'Fatou Sow'],
  },
  {
    code: 'CI', name: 'Côte d\'Ivoire', currency: 'XOF', locale: 'fr-CI', dialCode: '+225',
    gateways: [Paystack, Flutterwave],
    academicPeriod: 'term', schoolLevels: 'Primary & Secondary',
    examBodies: ['BEPC', 'Baccalauréat'],
    sampleNames: ['Kouassi Yao', 'Aya Koné', 'Konan Serge'],
  },
  {
    code: 'ZM', name: 'Zambia', currency: 'ZMW', locale: 'en-ZM', dialCode: '+260',
    gateways: [Flutterwave],
    academicPeriod: 'term', schoolLevels: 'Primary & Secondary',
    examBodies: ['ECZ'],
    sampleNames: ['Chanda Mwila', 'Mulenga Bwalya', 'Natasha Phiri'],
  },
  {
    code: 'EG', name: 'Egypt', currency: 'EGP', locale: 'en-EG', dialCode: '+20',
    gateways: [Flutterwave],
    academicPeriod: 'term', schoolLevels: 'Primary, Preparatory & Secondary',
    examBodies: ['Thanaweya Amma'],
    sampleNames: ['Omar Hassan', 'Mariam Adel', 'Youssef Samir'],
  },
  {
    code: 'GB', name: 'United Kingdom', inSentence: 'the United Kingdom', currency: 'GBP', locale: 'en-GB', dialCode: '+44',
    gateways: [],
    academicPeriod: 'term', schoolLevels: 'Primary & Secondary',
    examBodies: ['GCSE', 'A Level'],
    sampleNames: ['Oliver Bennett', 'Amelia Clarke', 'Harry Patel'],
  },
  {
    code: 'US', name: 'United States', inSentence: 'the United States', currency: 'USD', locale: 'en-US', dialCode: '+1',
    gateways: [],
    academicPeriod: 'semester', schoolLevels: 'K-12',
    examBodies: ['SAT', 'ACT', 'AP'],
    sampleNames: ['Ethan Johnson', 'Olivia Martinez', 'Noah Williams'],
  },
  {
    code: 'CA', name: 'Canada', currency: 'CAD', locale: 'en-CA', dialCode: '+1',
    gateways: [],
    academicPeriod: 'semester', schoolLevels: 'K-12',
    examBodies: ['Provincial assessments'],
    sampleNames: ['Liam Tremblay', 'Emma Roy', 'Lucas Singh'],
  },
  {
    code: 'IN', name: 'India', currency: 'INR', locale: 'en-IN', dialCode: '+91',
    gateways: [],
    academicPeriod: 'term', schoolLevels: 'Primary & Secondary',
    examBodies: ['CBSE', 'CISCE', 'State boards'],
    sampleNames: ['Aarav Sharma', 'Ananya Iyer', 'Rohan Gupta'],
  },
  {
    code: 'AE', name: 'United Arab Emirates', inSentence: 'the United Arab Emirates', currency: 'AED', locale: 'en-AE', dialCode: '+971',
    gateways: [],
    academicPeriod: 'term', schoolLevels: 'KG to Grade 12',
    examBodies: ['MoE', 'IGCSE', 'IB'],
    sampleNames: ['Omar Al Mansoori', 'Fatima Khan', 'Rahul Menon'],
  },
];

const BY_CODE = new Map(COUNTRY_PROFILES.map((profile) => [profile.code, profile]));

/** The profile for an ISO alpha-2 code, or the international fallback. */
export function getCountryProfile(code: string | null | undefined): CountryProfile {
  if (!code) return INTERNATIONAL_PROFILE;
  return BY_CODE.get(code.toUpperCase()) ?? INTERNATIONAL_PROFILE;
}

export function hasCountryProfile(code: string | null | undefined): boolean {
  return !!code && BY_CODE.has(code.toUpperCase());
}

/** Localised country name for any ISO code, profiled or not. */
export function countryName(code: string): string {
  const profiled = BY_CODE.get(code.toUpperCase());
  if (profiled) return profiled.name;
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(code.toUpperCase()) ?? code;
  } catch {
    return code;
  }
}

/** The country's name as it reads after "in": "in the United Kingdom". */
export const placeName = (profile: CountryProfile): string => profile.inSentence ?? profile.name;

/** Regional-indicator flag emoji for an ISO alpha-2 code. */
export function countryFlag(code: string): string {
  if (!/^[A-Za-z]{2}$/.test(code)) return '🌍';
  return String.fromCodePoint(...[...code.toUpperCase()].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
}

export const PAYMENT_GATEWAY_LABELS: Record<PaymentProviderType, string> = {
  [Paystack]: 'Paystack',
  [Flutterwave]: 'Flutterwave',
  [Remita]: 'Remita',
  [Interswitch]: 'Interswitch',
  [Monnify]: 'Monnify',
};

/**
 * Options for a country select: every profiled country, plus the visitor's
 * own country when it has no profile, so it is never silently replaced.
 */
export function countryOptions(current: string | null): { label: string; value: string }[] {
  const codes = COUNTRY_PROFILES.map((profile) => profile.code);
  if (current && !BY_CODE.has(current.toUpperCase())) codes.push(current.toUpperCase());
  return codes
    .map((code) => ({ code, name: countryName(code) }))
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(({ code, name }) => ({ label: `${countryFlag(code)} ${name}`, value: code }));
}
